import React from "react";

import { ChronoUnit, ZonedDateTime, convert } from "@js-joda/core";
import CalendarWrapper from "components/basic/calendar-wrapper/CalendarWrapper";
import CalendarHeatmap from "components/dashboard/calendar-heatmap/CalendarHeatmap";
import { AnalyticsResultDto, MdmObjectDto, useGetAnalyticsByObjectIdQuery, useGetAnalyticsByProjectIdAndDateRangeQuery } from "gql/generated/types";
import { ColumnFilterElementTemplateOptions } from "primereact/column";
import { Dropdown, DropdownChangeParams } from "primereact/dropdown";
import { Link, useParams } from "react-router-dom";
import { TableColumn } from "utils/types";
import { DEFAULT_DATE_FORMATTER } from "utils/constants";
import ItemsTable from "./ItemsTable";

type ObjectAnalyticsProps = {
  object?: MdmObjectDto;
  projectId?: string;
};

const ObjectAnalytics: React.FC<ObjectAnalyticsProps> = ({ object, projectId }) => {
  const halfYearAgo = convert(ZonedDateTime.now().truncatedTo(ChronoUnit.DAYS).minusMonths(6)).toDate();
  const dateNow = convert(ZonedDateTime.now()).toDate();
  const [dateFrom, setDateFrom] = React.useState<Date>(halfYearAgo);
  const [dateTo, setDateTo] = React.useState<Date>(dateNow);
  const [filterType, setFilterType] = React.useState<string | undefined>(undefined);
  const {projectId: proId} = useParams();


  const {data: analytics, loading} = useGetAnalyticsByObjectIdQuery({
    variables: {
      objectId: object?.id!,
      range: {
        start: dateFrom?.toISOString(),
        end: dateTo?.toISOString(),
        endInclusive: true,
        startInclusive: true
      }
    },
    skip: !object?.id
  })

  const {data, loading: loading2} = useGetAnalyticsByProjectIdAndDateRangeQuery({
    variables: {
      projectId: projectId!,
      range: {
        start: dateFrom?.toISOString(),
        end: dateTo?.toISOString(),
        endInclusive: true,
        startInclusive: true
      }
    },
    skip: !projectId
  })

  const analyticsData = (analytics?.allAnalyticsResultsByObjectIdAndDateRange ??
    data?.allAnalyticsResultsByProjectIdAndDateRange ??
    []) as AnalyticsResultDto[];
  const isLoading = loading || loading2;


  const handleTypeChange = (e: DropdownChangeParams, options: ColumnFilterElementTemplateOptions) => {
    options.filterApplyCallback(e.value)
    setFilterType(e.value);
  }

  const typeRowFilterTemplate = (options: ColumnFilterElementTemplateOptions) => {
    return (
      <Dropdown
        value={options.value}
        options={[...new Set(analyticsData.map(item => item.id?.analyticsType))]}
        onChange={e => handleTypeChange(e, options)}
        placeholder="Select One"
        showClear
      />
    );
  };
  
  const commonColumns: TableColumn<AnalyticsResultDto>[] = [
    {
      field: "id.date",
      header: "Date",
      sortable: true,
      body: rowData => ZonedDateTime.parse(rowData.id?.date).format(DEFAULT_DATE_FORMATTER),
    },
    {
      field: "id.analyticsType",
      header: "Type",
      sortable: true,
      filter: true,
      filterElement: typeRowFilterTemplate,
      showFilterMatchModes: false,
      onFilterClear: () => setFilterType(undefined),
    },
    { field: "description", header: "Description" },
  ];

  const renderObjectLink = (rowData: AnalyticsResultDto) => (
    <Link to={`/${proId}/objects/${rowData.mdmObject?.id}`}>{rowData.mdmObject?.title}</Link>
  );

  const columns: TableColumn<AnalyticsResultDto>[] = !object ? [
    ...commonColumns,
    { field: "mdmObject.id", header: "Object", body: renderObjectLink },
  ] : [...commonColumns];

  const formatHeatData = analyticsData.reduce((prev, curr) => {
    if (filterType && curr.id?.analyticsType !== filterType) return prev;
    const date = convert(ZonedDateTime.parse(curr.id?.date).truncatedTo(ChronoUnit.DAYS)).toDate();
    const heatDay = prev.find(day => day.date.getTime() === date.getTime());
    if (heatDay) {
      heatDay.value += 1;
    } else {
      prev.push({date, value: 1});
    }
    return prev;
  }, [] as { date: Date; value: number }[]);

  return (
    <div>
      <div className="flex gap-2 mb-5 z">
        <div className="flex flex-column gap-2">
          <label>Date from:</label>
          <CalendarWrapper value={dateFrom} showTime={false} onChange={date => setDateFrom(date)} />
        </div>
        <div className="flex flex-column gap-2">
          <label>Date to:</label>
          <CalendarWrapper value={dateTo} showTime={false} onChange={date => setDateTo(date)} />
        </div>
      </div>
      {formatHeatData.length > 0 && (
        <CalendarHeatmap
          startDate={dateFrom}
          endDate={dateTo}
          dataset={formatHeatData}
          setStartDate={setDateFrom}
          setEndDate={setDateTo}
          highValueColor="ed0000"
          lowValueColor="ffffff"
          minValue={0}
          maxValue={5}
          staticMinMaxColors
          staticMinMaxTitles={false}
          staticMinMaxValues
          isCustomDataset
        />
      )}
      <ItemsTable
        columns={columns}
        data={analyticsData}
        isLoading={isLoading}
        showToolbar={false}
      />
    </div>
  );
}

export default ObjectAnalytics;